/**
 * CR-8624: the Remarks cell on the customer's Stock Planning grid.
 *
 * Every customer user reads the remark, and hovering shows who last changed it
 * and when. A customer admin clicks the cell to type: Enter or clicking away
 * saves, Escape cancels, and saving it empty clears the remark. If the save
 * fails, the box stays open with the text so nothing typed is lost.
 */
import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { StockPlanningItem } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Input } from '../../ui/input';
import { Loader2, Pencil } from 'lucide-react';
import { remarkTitle } from './shortageColumns';

/** The longest remark the backend keeps. */
const MAX_REMARK_LENGTH = 255;

interface RemarkCellProps {
  item: StockPlanningItem;
  /** Saves the text; resolves true once saved. Left undefined, the cell is read-only. */
  onSave?: (item: StockPlanningItem, text: string) => Promise<boolean>;
  /** True while this row's remark is saving. */
  saving?: boolean;
}

export default function RemarkCell({ item, onSave, saving = false }: RemarkCellProps) {
  const { formatDateTime } = useFormatters();
  const [draft, setDraft] = useState<string | null>(null);
  // Enter saves, then the box disabling or closing blurs it; this stops the
  // blur sending the same save a second time.
  const inFlight = useRef(false);
  const remark = item.remarks || '';
  const title = remark
    ? remarkTitle(item.remark_updated_by_name, item.remark_updated_at, formatDateTime)
    : '';

  if (!onSave) {
    return <span title={title || undefined}>{remark}</span>;
  }

  const save = async () => {
    if (draft === null || saving || inFlight.current) return;
    if (draft.trim() === remark) {
      setDraft(null);
      return;
    }
    inFlight.current = true;
    try {
      if (await onSave(item, draft)) setDraft(null);
    } finally {
      inFlight.current = false;
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      setDraft(null);
    }
  };

  if (draft !== null) {
    return (
      <div className="flex items-center gap-1 min-w-[180px]">
        <Input
          autoFocus
          value={draft}
          maxLength={MAX_REMARK_LENGTH}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => void save()}
          aria-label={`Remark for ${item.part_number}`}
          placeholder="Type a remark — empty clears it"
          className="h-8 text-sm"
        />
        {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setDraft(remark)}
      title={title ? `${title} — click to edit` : 'Click to add a remark'}
      aria-label={remark ? `Edit remark for ${item.part_number}` : `Add a remark for ${item.part_number}`}
      className="w-full min-h-[1.5rem] text-left rounded px-1 -mx-1 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
    >
      {remark || <Pencil className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />}
    </button>
  );
}
